import React, { useState, useRef, useEffect } from 'react';
import { getDatabase, ref, set, onValue, remove } from 'firebase/database';
import html2pdf from 'html2pdf.js';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Delete, Download, Eye, Edit } from 'react-feather';
import { Modal, Button, ConfigProvider, Popconfirm, message } from 'antd';
import QRCode from 'qrcode';
import app from '../../firebase.js';

// Import images — signatures: src/img/rector.png, perez.png, bap_sig.png
import edaplogo from "../../img/edaplogo.png";
import crlogo from "../../img/crlogo.png";
import rectorSig from "../../img/rector.png";
import perezSig from "../../img/perez.png";
import bapSig from "../../img/bap_sig.png";
import './UNILISUNDMaster.css';

const UNILISUNDMasterTemplate = () => {
    const [formData, setFormData] = useState({
        noms: '',
        dateNaissance: '',
        lieuNaissance: '',
        province: '',
        pays: '',
        department: '',
        specialite: '',
        mention: 'Très Bien',
        anneeAcademique: '',
        numeroDoc: '',
        dateEmission: new Date().toISOString().split('T')[0]
    });
    const [documents, setDocuments] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [qrCodeData, setQrCodeData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const db = getDatabase(app);

    // Load documents from Firebase
    useEffect(() => {
        const documentsRef = ref(db, `unilis-und-master/EDAP/`);
        onValue(documentsRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const documentArray = Object.keys(data).map((key) => ({
                    id: key,
                    ...data[key],
                }));
                setDocuments(documentArray);
            }
        });
    }, [db]);

    // Generate QR Code
    useEffect(() => {
        const generateQRCode = async () => {
            if (!selectedDocument) return;
            
            let qrData = `UNILIS UND MASTER - EDAP\n`;
            Object.keys(selectedDocument).forEach(key => {
                if (key !== 'createdAt' && selectedDocument[key]) {
                    qrData += `${key}: ${selectedDocument[key]}\n`;
                }
            });
            qrData += `Status: Document Verified\nIssued by: Université de Lisala - UNILIS`;
            
            try {
                const qrCodeUrl = await QRCode.toDataURL(qrData, {
                    width: 120,
                    margin: 1,
                    color: { dark: '#000000', light: '#FFFFFF' }
                });
                setQrCodeData(qrCodeUrl);
            } catch (error) {
                console.error('Error generating QR code:', error);
            }
        };
        if (selectedDocument) generateQRCode();
    }, [selectedDocument]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Validate required fields
        const requiredFields = ['noms', 'dateNaissance', 'lieuNaissance', 'pays', 'department', 'specialite', 'mention', 'anneeAcademique', 'dateEmission'];
        for (const field of requiredFields) {
            if (!formData[field] || formData[field].trim() === '') {
                alert(`${field} est obligatoire!`);
                return;
            }
        }

        // Save to Firebase
        const documentRefPath = isEditing 
            ? ref(db, `unilis-und-master/EDAP/${editingId}`)
            : ref(db, `unilis-und-master/EDAP/${formData.noms.replace(/\s+/g, '_')}_${Date.now()}`);
        
        const dataToSave = isEditing 
            ? { ...formData }
            : { ...formData, createdAt: new Date().toISOString() };
        
        set(documentRefPath, dataToSave)
            .then(() => {
                console.log(isEditing ? 'UNILIS UND master updated successfully' : 'UNILIS UND master saved successfully');
                message.success(isEditing ? 'Document modifié avec succès' : 'Document créé avec succès');
                resetForm();
            })
            .catch((error) => {
                console.error('Error saving UNILIS UND master:', error);
                message.error(isEditing ? 'Erreur lors de la modification' : 'Erreur lors de la création');
            });
    };

    const showDocumentModal = (document) => {
        setSelectedDocument(document);
        setModalVisible(true);
    };

    const handleModalClose = () => {
        setModalVisible(false);
        setSelectedDocument(null);
    };

    const resetForm = () => {
        setFormData({
            noms: '',
            dateNaissance: '',
            lieuNaissance: '',
            province: '',
            pays: '',
            department: 'Sciences Juridiques',
            specialite: 'Droit des Affaires et Fiscalité',
            mention: 'Très Bien',
            anneeAcademique: '',
            numeroDoc: '',
            dateEmission: new Date().toISOString().split('T')[0]
        });
        setIsEditing(false);
        setEditingId(null);
    };

    const handleEditDocument = (document) => {
        setFormData({
            noms: document.noms || '',
            dateNaissance: document.dateNaissance || '',
            lieuNaissance: document.lieuNaissance || '',
            province: document.province || '',
            pays: document.pays || '',
            department: document.department || 'Sciences Juridiques',
            specialite: document.specialite || 'Droit des Affaires et Fiscalité',
            mention: document.mention || 'Très Bien',
            anneeAcademique: document.anneeAcademique || '',
            numeroDoc: document.numeroDoc || '',
            dateEmission: document.dateEmission || new Date().toISOString().split('T')[0]
        });
        setIsEditing(true);
        setEditingId(document.id);
        
        // Scroll to form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteDocument = (id) => {
        remove(ref(db, `unilis-und-master/EDAP/${id}`))
            .then(() => {
                message.success('Document supprimé avec succès');
            })
            .catch((error) => {
                console.error('Error deleting document:', error);
                message.error('Erreur lors de la suppression');
            });
    };

    const formatDateWithMonthName = (dateStr) => {
        const date = new Date(dateStr);
        const months = [
            "janvier", "février", "mars", "avril", "mai", "juin",
            "juillet", "août", "septembre", "octobre", "novembre", "décembre"
        ];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const downloadPDF = async () => {
        if (!selectedDocument) return;

        const element = document.getElementById('und-master-document');
        if (!element) {
            console.error('Document element not found');
            return;
        }

        const clone = element.cloneNode(true);
        clone.style.cssText = `
            width: 29.7cm !important;
            height: 21cm !important;
            margin: 0 auto !important;
            padding: 2cm !important;
            background: white !important;
            position: relative !important;
            box-sizing: border-box !important;
            font-family: 'Times New Roman', serif !important;
            display: flex !important;
            flex-direction: column !important;
        `;

        const tempContainer = document.createElement('div');
        tempContainer.style.cssText = `
            position: fixed !important;
            top: -9999px !important;
            left: 50% !important;
            transform: translateX(-50%) !important;
            z-index: -1 !important;
            width: 29.7cm !important;
            background: white !important;
        `;
        tempContainer.appendChild(clone);
        document.body.appendChild(tempContainer);

        try {
            // Wait for images to load
            const images = clone.querySelectorAll('img');
            await Promise.all(Array.from(images).map(img => {
                return new Promise((resolve) => {
                    if (img.complete) {
                        resolve();
                    } else {
                        img.onload = resolve;
                        img.onerror = resolve;
                    }
                });
            }));

            // Small delay to ensure rendering is complete
            await new Promise(resolve => setTimeout(resolve, 500));

            // A4 landscape dimensions: 29.7cm x 21cm = 842pt x 595pt
            // At 96 DPI: 29.7cm = 1123px, 21cm = 794px
            // Double the scale for better quality
            const scale = 4;
            const A4_LANDSCAPE_WIDTH_PX = 1123;
            const A4_LANDSCAPE_HEIGHT_PX = 794;

            const canvas = await html2canvas(clone, {
                scale: scale,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#FFFFFF',
                width: A4_LANDSCAPE_WIDTH_PX,
                height: A4_LANDSCAPE_HEIGHT_PX,
                logging: false,
                imageTimeout: 10000,
                windowWidth: A4_LANDSCAPE_WIDTH_PX,
                windowHeight: A4_LANDSCAPE_HEIGHT_PX
            });
            
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('l', 'pt', 'a4'); // 'l' for landscape
            const pageWidth = pdf.internal.pageSize.getWidth(); // 842pt
            const pageHeight = pdf.internal.pageSize.getHeight(); // 595pt

            // Scale image to fill page with left margin
            const imgWidth = canvas.width / scale;
            const imgHeight = canvas.height / scale;
            const widthRatio = pageWidth / imgWidth;
            const heightRatio = pageHeight / imgHeight;
            // Slightly reduce vertical scale to prevent cutting signatures
            const ratio = Math.min(widthRatio, heightRatio * 0.95) * 1.2;
            
            const scaledWidth = imgWidth * ratio;
            const scaledHeight = imgHeight * ratio;
            const xOffset = 20; // Left margin in points
            const yOffset = 20; // Top margin in points

            pdf.addImage(imgData, 'PNG', xOffset, yOffset, scaledWidth, scaledHeight);
            pdf.save(`${selectedDocument.noms?.replace(/\s+/g, '_') || 'document'}_UNILIS_UND_master.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            message.error('Erreur lors de la génération du PDF');
        } finally {
            document.body.removeChild(tempContainer);
        }
    };

    return (
        <div className="und-master-diploma-container">
            <div className="und-master-form-section">
                <div style={{ textAlign: 'center' }}>
                    <img src={edaplogo} alt="Logo UNILIS" className="und-master-form-logo" />
                    <h2>Formulaire de Master UND - Université de Lisala</h2>
                </div>

                <form onSubmit={handleSubmit} className="und-master-form">
                    <div className="und-master-form-row">
                        <input
                            type="text"
                            name="noms"
                            value={formData.noms}
                            onChange={handleInputChange}
                            placeholder="Nom complet"
                            required
                        />
                        <input
                            type="date"
                            name="dateNaissance"
                            value={formData.dateNaissance}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="und-master-form-row">
                        <input
                            type="text"
                            name="lieuNaissance"
                            value={formData.lieuNaissance}
                            onChange={handleInputChange}
                            placeholder="Lieu de naissance"
                            required
                        />
                        <input
                            type="text"
                            name="province"
                            value={formData.province}
                            onChange={handleInputChange}
                            placeholder="Province (optionnel)"
                        />
                    </div>

                    <div className="und-master-form-row">
                        <input
                            type="text"
                            name="pays"
                            value={formData.pays}
                            onChange={handleInputChange}
                            placeholder="Pays"
                            required
                        />
                        <input
                            type="text"
                            name="department"
                            value={formData.department}
                            onChange={handleInputChange}
                            placeholder="Département (ex: Sciences Juridiques)"
                            required
                        />
                    </div>
                    
                    <div className="und-master-form-row">
                        <input
                            type="text"
                            name="specialite"
                            value={formData.specialite}
                            onChange={handleInputChange}
                            placeholder="Spécialité"
                            required
                        />
                        <select
                            name="mention"
                            value={formData.mention}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="Excellent">Excellent</option>
                            <option value="Très Bien">Très Bien</option>
                            <option value="Bien">Bien</option>
                            <option value="Assez Bien">Assez Bien</option>
                            <option value="Passable">Passable</option>
                        </select>
                    </div>

                    <div className="und-master-form-row">
                        <input
                            type="text"
                            name="anneeAcademique"
                            value={formData.anneeAcademique}
                            onChange={handleInputChange}
                            placeholder="Année académique (Ex: 2024-2025)"
                            required
                        />
                        <input
                            type="text"
                            name="numeroDoc"
                            value={formData.numeroDoc}
                            onChange={handleInputChange}
                            placeholder="Numéro du document (optionnel)"
                        />
                    </div>

                    <div className="und-master-form-row">
                        <input
                            type="date"
                            name="dateEmission"
                            value={formData.dateEmission}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <button type="submit" className="und-master-submit-btn">
                        {isEditing ? 'Modifier le Master UND - UNILIS' : 'Créer le Master UND - UNILIS'}
                    </button>
                    {isEditing && (
                        <button 
                            type="button" 
                            onClick={resetForm}
                            className="und-master-submit-btn"
                            style={{ 
                                marginTop: '10px', 
                                background: 'linear-gradient(135deg, #6c757d, #5a6268)' 
                            }}
                        >
                            Annuler
                        </button>
                    )}
                </form>

                {/* Document List */}
                <div className="und-master-document-list">
                    {documents.map((document) => (
                        <div key={document.id} className="und-master-document-item">
                            <button
                                className="und-master-document-toggle"
                                onClick={() => showDocumentModal(document)}
                            >
                                <Eye size={16} style={{ marginRight: '8px' }} />
                                {document.noms} - UNILIS UND Master
                            </button>
                            <button
                                className="und-master-document-toggle"
                                onClick={() => handleEditDocument(document)}
                                style={{ 
                                    marginRight: '8px', 
                                    background: '#ffc107',
                                    color: '#000'
                                }}
                            >
                                <Edit size={16} style={{ marginRight: '8px' }} />
                                Modifier
                            </button>
                            <Popconfirm
                                title="Supprimer le document"
                                description="Êtes-vous sûr de vouloir supprimer ce document ?"
                                onConfirm={() => handleDeleteDocument(document.id)}
                                okText="Oui"
                                cancelText="Non"
                                okButtonProps={{ danger: true }}
                            >
                                <button className="und-master-delete-btn">
                                    <Delete size={15} />
                                </button>
                            </Popconfirm>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modal */}
            <ConfigProvider>
                <Modal
                    title={<span style={{ color: 'white' }}>Aperçu - Master UND UNILIS</span>}
                    open={modalVisible}
                    onCancel={handleModalClose}
                    width="95vw"
                    style={{ top: 10 }}
                    centered={false}
                    maskClosable={true}
                    keyboard={true}
                    footer={[
                        <Button
                            key="download"
                            type="primary"
                            icon={<Download size={16} />}
                            onClick={downloadPDF}
                            style={{
                                backgroundColor: '#28a745',
                                borderColor: '#28a745',
                                fontWeight: 'bold'
                            }}
                        >
                            Télécharger PDF
                        </Button>,
                        <Button
                            key="close"
                            onClick={handleModalClose}
                            style={{ fontWeight: 'bold' }}
                        >
                            Fermer
                        </Button>
                    ]}
                >
                    {selectedDocument && (
                        <div className="und-master-modal-content">
                            <UNILISUNDMasterDocument document={selectedDocument} qrCodeData={qrCodeData} />
                        </div>
                    )}
                </Modal>
            </ConfigProvider>
        </div>
    );
};

// Document Component
const UNILISUNDMasterDocument = ({ document: docData, qrCodeData }) => {
    const formatDateWithMonthName = (dateStr) => {
        const date = new Date(dateStr);
        const months = [
            "janvier", "février", "mars", "avril", "mai", "juin",
            "juillet", "août", "septembre", "octobre", "novembre", "décembre"
        ];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    return (
        <div id="und-master-document" className="und-master-diploma-document">
            {/* Header */}
            <div className="und-master-header">
                <div className="und-master-header-row">
                    <div className="und-master-left-logo">
                        <img src={edaplogo} alt="Logo Left" className="und-master-logo" />
                    </div>
                    <div className="und-master-header-center">
                        <h1 className="und-master-country">REPUBLIQUE DEMOCRATIQUE DU CONGO</h1>
                        <h2 className="und-master-university">UNIVERSITE DE LISALA</h2>
                        <p className="und-master-address" style={{ marginTop: '8px', fontSize: '12px' }}>Avenue Aérodrome n° 20 , Quartier 3C, Commune de Bolikango, Ville de Lisala, Province de la Mongala</p>
                        <p className="und-master-address" style={{ fontSize: '12px' }}>P.O. Box 9289 Kinshasa I</p>
                        <p className="und-master-contact" style={{ fontSize: '12px' }}>Web: www.universitedelisala.ac.cd ; Email: info@universitedelisala.ad.cd</p>
                    </div>
                    <div className="und-master-right-logo">
                        <img src={crlogo} alt="Logo Right" className="und-master-logo" />
                    </div>
                </div>
            </div>

            {/* Title Section */}
            <div className="und-master-title-section">
                <div className="und-master-title-row">
                    <div className="und-master-title-spacer"></div>
                    <h1 className="und-master-diploma-title">DIPLOME DE MASTER</h1>
                    <div className="und-master-number-box">
                        <span className="und-master-number-label">N°</span>
                        <span className="und-master-number-value">{docData.numeroDoc || ''}</span>
                    </div>
                </div>
            </div>

            {/* Document Body */}
            <div className="und-master-document-body">
                <div className="und-master-legal-references">
                <p className="und-master-vu-clause">Vu la Loi-cadre n°14/004 du 11 février 2014 de l'Enseignement National;</p>
                <p className="und-master-vu-clause">Vu l'arrêté ministériel n° 133/MINESU/CAB.MIN/NM/RS/2006 du 25 avril 2006 portant création et prise en charge par le trésor public d'un Centre Universitaire qualifié « Centre Universitaire de Lisala » ; </p>
                <p className="und-master-vu-clause">Vu l'arrêté ministériel n° 246/MINESU/CAB.MIN/MML/CB/JN/2010 du 02/12/2010, portant autonomisation du Centre Universitaire de Lisala ; </p>
                <p className="und-master-vu-clause">Vu les accords de partenariats de cotutelle internationale de thèses et Co diplomation signés entre le Centre Interuniversitaire de Recherche Pluridisciplinaire (CIREP) de l'Université de Lisala en RDC, Groupe de Recherche Appliquée pour le Développement par l'Innovation de l'Afrique-GRADIAF en France; Université IRGIB AFRICA au Benin et UNCR University;</p>
                <p className="und-master-vu-clause">Vu les procès-verbaux du Jury attestant que l'intéressé a satisfait au contrôle des compétences prévu par les textes légaux et réglementaires;</p>
                </div>

                <div className="und-master-diploma-declaration">
                    <p className="und-master-main-declaration">
                        Le diplôme de Master en <strong>{docData.department}</strong> Spécialité: <strong>{docData.specialite}</strong> est délivré à <strong>{docData.noms}</strong> né le <strong>{formatDateWithMonthName(docData.dateNaissance)}</strong> à <strong>{docData.lieuNaissance}</strong>{docData.province ? ` Province du ${docData.province}` : ''}/<strong>{docData.pays}</strong> au titre de l'année académique <strong>{docData.anneeAcademique}</strong> avec la mention <strong>{docData.mention}</strong> et lui confère le grade de Master (Mphil), pour en jouir avec les droits et prérogatives qui y sont attachés.
                    </p>
                </div>

                <div className="und-master-emission-date">
                    <p>Fait à Lisala, le <strong>{formatDateWithMonthName(docData.dateEmission)}</strong></p>
                </div>
            </div>

            {/* Signatures: left → right — Recteur (rector.png), Directeur ED (perez.png), DG CIREP (bap_sig.png) */}
            <div className="und-master-signatures">
                <div className="und-master-signature-row">
                    <div className="und-master-signature-item">
                        <img src={rectorSig} alt="" className="und-master-signature-img" />
                        <hr className="und-master-signature-line-separator" />
                        <p className="und-master-signature-name">Professeur Abbé Donat TEBAKABE Alomo</p>
                        <p className="und-master-signature-title">Le Recteur</p>
                    </div>
                    <div className="und-master-signature-item">
                        <img src={perezSig} alt="" className="und-master-signature-img" />
                        <hr className="und-master-signature-line-separator" />
                        <p className="und-master-signature-name">Pr Jean Luc Perez</p>
                        <p className="und-master-signature-title">Directeur de l&apos;École Doctorale/UNCR University</p>
                    </div>
                    <div className="und-master-signature-item">
                        <img src={bapSig} alt="" className="und-master-signature-img" />
                        <hr className="und-master-signature-line-separator" />
                        <p className="und-master-signature-name">Pr NIZEYIMANA Jean Baptiste</p>
                        <p className="und-master-signature-title">Directeur Général du CIREP</p>
                    </div>
                </div>
                {qrCodeData && (
                    <div className="und-master-qr-section">
                        <img
                            src={qrCodeData}
                            alt="QR Code - Document Verification"
                            className="und-master-qr-code"
                            style={{
                                width: '60px',
                                height: '60px',
                                border: '1px solid #000'
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default UNILISUNDMasterTemplate;